import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";

import * as Ai from "react-icons/ai";
import * as Io from "react-icons/io";
import * as Lu from "react-icons/lu";
import * as Md from "react-icons/md";

import ReservationMessages from "../components/custom/ReservationMessages";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import Utils from "../utils/Utils";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { Modal } from "antd";
import { PulseLoader } from "react-spinners";
import "../styles/style.css";

function ViewReservation() {
  const { id } = useParams();
  const {
    fetchReservationById,
    tokenData,
    reservationLoading,
    updateEnquiry,
    enquiryActionLoading,
    fetchNewReservations,
    fetchFutureReservations,
    getMessages,
    reservationSettings,
    tableLists,
    todaysReservationList,
    reservFoodpageTiming,
    foodpageReservSettings,
    closureList,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const [reservDetails, setReservDetails] = useState({
    userID: "",
    name: "",
    email: "",
    phone: "",
    bookingDate: "",
    bookingTime: "",
    formattedID: "",
    status: "",
    chairs: 0,
    message: "",
    tables: null,
  });
  const [showModal, setShowModal] = useState(false);
  const [expired, setExpired] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isError, setIsError] = useState(false);
  const [messageList, setMessageList] = useState(null);
  const [errors, setErrors] = useState({});

  const [cancelAllowDate, setCancelAllowDate] = useState(null);
  const [timeIntervals, setTimeIntervals] = useState(null);

  useEffect(() => {
    if (tokenData) {
      fetchDetails();
    }
  }, [tokenData]);

  useEffect(() => {
    if (!reservDetails?.bookingDate) return;

    const today = new Date();

    const bookedDate = new Date(reservDetails.bookingDate);
    const bookedDateOnly = new Date(
      bookedDate.getFullYear(),
      bookedDate.getMonth(),
      bookedDate.getDate()
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

    if (bookedDateOnly < todayOnly) {
      console.log("Past date. Expired.");
      setExpired(true);
      return;
    }

    if (bookedDateOnly.getTime() === todayOnly.getTime()) {
      console.log("Same date. Checking time...");
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const currentTime = `${hours}:${minutes}:${seconds}`;

      const bookingTime = reservDetails?.bookingTime;
      if (bookingTime < currentTime) {
        setExpired(true);
        return;
      }
    }

    setExpired(false);
  }, [reservDetails]);

  useEffect(() => {
    if (!tokenData || !id) return;

    getNewMessages();
  }, [tokenData]);

  useEffect(() => {
    if (!reservDetails?.bookingDate) return;

    getReservTiming(reservDetails?.bookingDate);
  }, [reservDetails?.bookingDate]);

  const getClosureWindowsForDate = (date) => {
    if (!Array.isArray(closureList) || closureList.length === 0) return [];

    const dateObj = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObj.getTime())) return [];

    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const targetDateStr = `${dateObj.getFullYear()}-${pad(
      dateObj.getMonth() + 1
    )}-${pad(dateObj.getDate())}`;

    return closureList
      .filter((c) => c.status === "Active")
      .map((c) => {
        const startDateStr = c.startTime.slice(0, 10);
        const endDateStr = c.endTime.slice(0, 10);
        const startTimeStr = c.startTime.slice(11, 16);
        const endTimeStr = c.endTime.slice(11, 16);

        if (targetDateStr < startDateStr || targetDateStr > endDateStr) {
          return null;
        }

        let dayStart;
        let dayEnd;

        if (startDateStr === endDateStr) {
          // single-day closure
          dayStart = startTimeStr;
          dayEnd = endTimeStr;
        } else if (targetDateStr === startDateStr) {
          // first day of multi-day closure
          dayStart = startTimeStr;
          dayEnd = "23:59";
        } else if (targetDateStr === endDateStr) {
          // last day of multi-day closure
          dayStart = "00:00";
          dayEnd = endTimeStr;
        } else {
          // middle day of multi-day closure
          dayStart = "00:00";
          dayEnd = "23:59";
        }

        return { startTime: dayStart, endTime: dayEnd };
      })
      .filter(Boolean);
  };

  const getReservTiming = (date) => {
    if (!reservFoodpageTiming) return;

    setReservDetails((prev) => {
      const isDateChanged = prev.bookingDate && prev.bookingDate !== date;

      return {
        ...prev,
        bookingDate: date,
        bookingTime: isDateChanged ? "" : prev.bookingTime,
      };
    });

    const dayValue = Utils.getDayOfWeek(date);
    const todaysTiming = reservFoodpageTiming[dayValue];

    const intervalDuration =
      typeof foodpageReservSettings?.timeinterval === "string"
        ? parseInt(foodpageReservSettings?.timeinterval, 10)
        : 30;

    if (!todaysTiming || todaysTiming.length === 0) {
      setTimeIntervals([]);
      return;
    }

    //! Generate all intervals from opening/closing time
    const allIntervals = todaysTiming.flatMap((time) => {
      return Utils.getReservIntervals(
        time.openingTime,
        time.closingTime,
        intervalDuration
      );
    });

    //! Get closure window(s) for this date
    const closureWindows = getClosureWindowsForDate(date, closureList);

    if (!closureWindows || (closureWindows && closureWindows.length == 0)) {
      setTimeIntervals(allIntervals);
      return;
    }

    //! Filter out intervals that fall inside any closure window
    const filteredIntervals = allIntervals.filter((time) => {
      return closureWindows.every((win) => {
        const start = win.startTime;
        const end = win.endTime;
        return time < start || time > end;
      });
    });

    setTimeIntervals(filteredIntervals);
  };

  useEffect(() => {
    if (!reservDetails?.bookingDate || reservDetails?.bookingDate.length === 0)
      return;
    getReservTiming(reservDetails?.bookingDate);
  }, [reservDetails?.bookingDate]);

  const getNewMessages = async () => {
    const payload = {
      reservationID: id,
    };
    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    await getMessages(payload, {
      onSuccess: (res) => {
        if (res?.data) {
          setMessageList(res?.data.chatMessages);
        }
      },
      onFailed: (err) => {
        console.log("Error on fetching messages");
      },
      headers: headers,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReservDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleChairs = (value) => {
    const numberRegex = /^[0-9]*$/;
    if (numberRegex.test(value)) {
      setReservDetails((prev) => ({ ...prev, chairs: value }));
    }
  };

  const tablesDetails = useMemo(() => {
    if (!Array.isArray(tableLists) || tableLists.length === 0) return [];

    let tableIds = reservDetails?.tables;
    if (!tableIds) return [];

    if (typeof tableIds === "string") {
      try {
        tableIds = JSON.parse(tableIds);
      } catch {
        return [];
      }
    }

    if (!Array.isArray(tableIds)) return [];

    return tableIds
      .map(
        (tableId) =>
          tableLists.find((t) => t.id === tableId || t.id == tableId) || null
      )
      .filter(Boolean);
  }, [tableLists, reservDetails?.tables]);

  const fetchDetails = async () => {
    const payload = {
      reservationID: id,
    };
    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    await fetchReservationById(payload, {
      onSuccess: (res) => {
        if (res?.data && res?.data.reservationProfile) {
          const data = res?.data?.reservationProfile;
          const [date, time] = data?.bookingTime.split(" ");
          console.log("data", data);

          setReservDetails({
            userID: data?.userID,
            name: data?.name,
            email: data?.email,
            phone: data?.phone,
            bookingDate: date,
            bookingTime: time,
            chairs: data?.chairs,
            formattedID: data?.formattedID,
            message: data?.message,
            status: data?.status,
            tables: data?.tables,
          });
        }
      },
      onFailed: (err) => {
        console.log("fetch error", err);
      },
      headers: headers,
    });
  };

  const handleApproveReservation = async () => {
    if (!reservDetails) return;

    const { status, ...rest } = reservDetails;
    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    const payload = {
      ...rest,
      status: "Approved",
      advancePayment: "",
      advanceAmount: "",
      amountStatus: "",
      shopMessage: "",
    };

    await updateEnquiry(id, payload, {
      onSuccess: async (res) => {
        toast.success("Reservation is Approved!");
        await fetchNewReservations(tokenData?.shopCustomToken);
        await fetchFutureReservations(tokenData?.shopCustomToken);
        setTimeout(() => {
          // win("/table-enquiry-list");
          window.location.reload("/table-enquiry-list");
        }, 1000);
      },
      onFailed: (err) => {
        fetchDetails();
        const msg =
          err?.messages ?? "Reservation is not approved! Please try again";
        toast.error(msg);
      },
      headers: headers,
    });
  };

  const handleRejectReservation = async () => {
    if (!reservDetails) return;

    const { status, ...rest } = reservDetails;
    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    const payload = {
      ...rest,
      status: "Cancelled",
      advancePayment: "",
      advanceAmount: "",
      amountStatus: "",
      shopMessage: "",
    };

    await updateEnquiry(id, payload, {
      onSuccess: async (res) => {
        toast.success("Reservation is Cancelled!");
        await fetchNewReservations(tokenData?.shopCustomToken);
        await fetchFutureReservations(tokenData?.shopCustomToken);
        setTimeout(() => {
          window.location.reload("/table-enquiry-list");
        }, 1000);
      },
      onFailed: (err) => {
        console.log("Error", err);
        const msg =
          err?.messages ?? "Reservation is not approved! Please try again";
        toast.error(msg);
        fetchDetails();
      },
      headers: headers,
    });
  };

  const validateFields = () => {
    for (const key in reservDetails) {
      if (key !== "message") {
        const value = reservDetails[key];

        if (
          (typeof value === "string" && !value.trim()) ||
          (typeof value === "number" && value <= 0)
        ) {
          setIsError(true);
          return true;
        }
      }
    }

    setIsError(false);
    return false;
  };

  const isBookingValid = () => {
    const bookingDate = reservDetails?.bookingDate;
    const bookingTime = reservDetails?.bookingTime;
    const now = new Date();

    const bookingDateNew = new Date(bookingDate);

    const isSameDay =
      now.getDate() === bookingDateNew.getDate() &&
      now.getMonth() === bookingDateNew.getMonth() &&
      now.getFullYear() === bookingDateNew.getFullYear();

    if (isSameDay) {
      const [hours, minutes] = bookingTime.split(":").map(Number);

      const bookingDateTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes
      );

      return bookingDateTime < now;
    }

    return false;
  };

  const validateReservation = (reservDetails) => {
    let errors = {};

    if (!reservDetails.name.trim()) {
      errors.name = "Name is required.";
    }

    if (!reservDetails.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reservDetails.email)) {
      errors.email = "Invalid email format.";
    }

    if (!reservDetails.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else if (!/^\d{10}$/.test(reservDetails.phone)) {
      errors.phone = "Phone number must be 10 digits.";
    }

    if (!reservDetails.bookingDate.trim()) {
      errors.bookingDate = "Booking date is required.";
    }

    if (!reservDetails.bookingTime.trim()) {
      errors.bookingTime = "Booking time is required.";
    }

    if (!reservDetails.chairs || reservDetails.chairs <= 0) {
      errors.chairs = "Number of chairs must be at least 1.";
    }

    return errors;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateFields();

    // const validationErrors = validateReservation(reservDetails);

    // if (Object.keys(validationErrors).length > 0) {
    //   setErrors(validationErrors);
    //   return;
    // }
    if (isValid == true) return;
    const isValidBookingTIme = isBookingValid();

    if (isValidBookingTIme === true) {
      toast.error(`Please select a valid time!`);
      return;
    }
    if (reservDetails?.chairs > 50) {
      toast.error("Party size is must be less than 50");
      return;
    }
    if (reservDetails?.chairs <= 0) {
      toast.error("Please choose minimum party size!");
      return;
    }

    const headers = { "x-shopToken": tokenData?.shopCustomToken };
    const payload = {
      ...reservDetails,
      advancePayment: "",
      advanceAmount: "",
      amountStatus: "",
      shopMessage: "",
    };

    await updateEnquiry(id, payload, {
      onSuccess: async (res) => {
        toast.success("Reservation Details updates successfully!");
        await fetchNewReservations(tokenData?.shopCustomToken);
        await todaysReservationList(tokenData?.shopCustomToken);
        await fetchFutureReservations(tokenData?.shopCustomToken);
        setIsEdit(false);
      },
      onFailed: (err) => {
        console.log("Error", err);
        const msg = err?.messages ?? "Updation failed! Please try again";
        toast.error(msg);
        setIsEdit(false);
        fetchDetails();
      },
      headers: headers,
    });
  };

  const today = new Date().toISOString().split("T")[0];

  const handleRefresh = async () => {
    await getNewMessages();
  };

  return (
    <Fragment>
      <MasterLayout>
        <Modal
          onCancel={() => setShowModal(false)}
          open={showModal}
          footer={null}
        >
          <h3 className="title_">Are You sure?</h3>
          <div className="btn_grp">
            <button
              type="button"
              className="view_btns_ reject_btn_"
              onClick={handleRejectReservation}
              disabled={enquiryActionLoading}
            >
              Yes
            </button>
            <button
              type="button"
              className="view_btns_ discard_btn_ ms-3"
              onClick={() => setShowModal(false)}
            >
              No
            </button>
          </div>
        </Modal>
        <button type="button" className="back_btn" onClick={() => navigate(-1)}>
          <Md.MdKeyboardArrowLeft /> Back
        </button>
        <div className="card view_reserv_card h-100 p-3 radius-12 custom_card position-relative">
          <button
            type="button"
            className="refresh_btn view_reserv_refresh"
            onClick={handleRefresh}
          >
            <Io.IoMdRefresh /> <span>Refresh</span>
          </button>
          <Tabs className="view_reserv_tabs">
            <TabList>
              <Tab>Reservation Details</Tab>
              <Tab>Messages</Tab>
            </TabList>
            <TabPanel>
              {reservDetails?.status == "Requested" && expired === false && (
                <div className="edit_btns">
                  {isEdit === false ? (
                    <button
                      type="button"
                      className="edit_reserv"
                      onClick={() => setIsEdit(true)}
                    >
                      <i>
                        <Lu.LuClipboardEdit />
                      </i>
                      <span>Edit</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="close_btn"
                      onClick={() => setIsEdit(false)}
                    >
                      <i>
                        <Ai.AiOutlineCloseCircle />
                      </i>
                      <span>Close</span>
                    </button>
                  )}
                </div>
              )}
              {!reservationLoading && expired == true && (
                <div class="alert alert-warning text-center" role="alert">
                  This reservation is expired!
                </div>
              )}
              <div className="card-body p-24">
                {!reservationLoading ? (
                  <>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="mb-0">
                        #{reservDetails?.formattedID ?? "N/A"}
                      </h6>
                      <span
                        className={
                          reservDetails?.status === "Requested"
                            ? "badge rounded-pill text-bg-primary"
                            : reservDetails?.status === "Modified"
                            ? "badge rounded-pill text-bg-warning"
                            : reservDetails?.status === "Approved"
                            ? "badge rounded-pill text-bg-success"
                            : reservDetails?.status === "Cancelled"
                            ? "badge rounded-pill text-bg-danger"
                            : "badge rounded-pill text-bg-dark"
                        }
                      >
                        {reservDetails?.status ?? "N/A"}
                      </span>
                    </div>
                    <br />
                  </>
                ) : (
                  <h6>
                    <Skeleton width={150} />
                  </h6>
                )}
                <div className="container view_reserv_details">
                  {!isEdit ? (
                    <div className="table-responsive">
                    <table className="table bordered-table view_reserv_table">
                      <tbody>
                        <tr>
                          <td className="key_">Date</td>
                          <td>
                            {!reservationLoading ? (
                              <>
                                {reservDetails?.bookingDate
                                  ? Utils.formatDate(reservDetails?.bookingDate)
                                  : "N/A"}
                              </>
                            ) : (
                              <Skeleton width={200} height={20} />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="key_">Time</td>
                          <td>
                            {!reservationLoading ? (
                              <>
                                {reservDetails?.bookingTime
                                  ? Utils.convertTiming(
                                      reservDetails?.bookingTime
                                    )
                                  : "N/A"}
                              </>
                            ) : (
                              <Skeleton width={200} height={20} />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="key_">Name</td>
                          <td>
                            {!reservationLoading ? (
                              <>{reservDetails?.name ?? "N/A"}</>
                            ) : (
                              <Skeleton width={200} height={20} />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="key_">Phone number</td>
                          <td>
                            {!reservationLoading ? (
                              <>{reservDetails?.phone ?? "N/A"}</>
                            ) : (
                              <Skeleton width={200} height={20} />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="key_">Email</td>
                          <td>
                            {!reservationLoading ? (
                              <>{reservDetails?.email ?? "N/A"}</>
                            ) : (
                              <Skeleton width={200} height={20} />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="key_">Party Size</td>
                          <td>
                            {!reservationLoading ? (
                              <>{reservDetails?.chairs ?? "N/A"}</>
                            ) : (
                              <Skeleton width={200} height={20} />
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="key_">Custom Message</td>
                          <td>
                            {!reservationLoading ? (
                              <>{reservDetails?.message ?? "N/A"}</>
                            ) : (
                              <Skeleton width={200} height={20} />
                            )}
                          </td>
                        </tr>

                        {expired == true && (
                          <tr>
                            <td className="key_">Status</td>
                            <td>{reservDetails?.status ?? "N/A"}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    </div>
                  ) : (
                    <>
                      <form action="" onSubmit={handleEditSubmit}>
                        <p className="sub_title_">Booking Information</p>
                        <div className="row mb-3">
                          <div className="col-lg-6 col-md-6 col-sm-12 mt-2">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Booking Date
                              </label>
                              <input
                                type="date"
                                name="bookingDate"
                                id=""
                                className="form-control"
                                value={reservDetails?.bookingDate}
                                onChange={(e) => {
                                  getReservTiming(e.target.value);
                                }}
                                min={today}
                              />
                              {isError &&
                                reservDetails?.bookingDate.length == 0 && (
                                  <p className="err__msg">
                                    Booking Date is required!
                                  </p>
                                )}
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12 mt-2">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Booking Time
                              </label>
                              <select
                                name="bookingTime"
                                id=""
                                className="form-select"
                                onChange={handleChange}
                                value={
                                  reservDetails?.bookingTime
                                    ? reservDetails.bookingTime.slice(0, 5)
                                    : ""
                                }
                              >
                                <option value="">Choose booking time</option>
                                {timeIntervals &&
                                  timeIntervals.length != 0 &&
                                  timeIntervals.map((interval, index) => {
                                    return (
                                      <option value={interval} key={index}>
                                        {interval
                                          ? Utils.convertTiming(interval)
                                          : "N/A"}
                                      </option>
                                    );
                                  })}
                              </select>
                              {isError &&
                                reservDetails?.bookingTime.length == 0 && (
                                  <p className="err__msg">
                                    Booking Time is required!
                                  </p>
                                )}
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12 mt-2">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Party Size
                              </label>
                              <input
                                type="text"
                                name="chairs"
                                id=""
                                className="form-control"
                                value={reservDetails?.chairs}
                                onChange={(e) => handleChairs(e.target.value)}
                                maxLength={50}
                              />
                              {isError && reservDetails?.chairs.length == 0 && (
                                <p className="err__msg">
                                  Party size is required!
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12 mt-2">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Status
                              </label>
                              <select
                                name="status"
                                id=""
                                className="form-select"
                                onChange={handleChange}
                                value={reservDetails?.status}
                              >
                                <option value="Requested">Requested</option>
                                <option value="Approved" selected>
                                  Approved
                                </option>
                                <option value="Rejected">Rejected</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="Completed">Completed</option>
                              </select>
                            </div>
                          </div>
                        </div>
                        <p className="sub_title_">Personal Information</p>
                        <div className="row">
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group">
                              <label htmlFor="" className="form-label">
                                Name
                              </label>
                              <input
                                type="text"
                                name="name"
                                id=""
                                className="form-control"
                                value={reservDetails?.name}
                                onChange={handleChange}
                              />
                              {isError && reservDetails?.name.length == 0 && (
                                <p className="err__msg">Name is required!</p>
                              )}
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="form-group ">
                              <label htmlFor="" className="form-label">
                                Phone Number
                              </label>
                              <input
                                type="text"
                                name="phone"
                                id=""
                                className="form-control"
                                value={reservDetails?.phone}
                                onChange={handleChange}
                              />
                              {isError && reservDetails?.phone.length == 0 && (
                                <p className="err__msg">
                                  Phone Number is required!
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="form-group mt-3">
                          <label htmlFor="" className="form-label">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            id=""
                            className="form-control"
                            value={reservDetails?.email}
                            onChange={handleChange}
                          />
                          {isError && reservDetails?.email.length == 0 && (
                            <p className="err__msg">
                              Email Address is required!
                            </p>
                          )}
                        </div>

                        <div className="form-group mt-3">
                          <label htmlFor="" className="form-label">
                            Custom Message
                          </label>
                          <textarea
                            name="message"
                            id=""
                            className="form-control"
                            value={reservDetails?.message}
                            onChange={handleChange}
                          ></textarea>
                        </div>
                        <br />
                        <button
                          type="submit"
                          className="submit_btn text-uppercase"
                          disabled={enquiryActionLoading}
                        >
                          {!enquiryActionLoading ? (
                            "Update"
                          ) : (
                            <PulseLoader color="#fff" size={10} />
                          )}
                        </button>
                      </form>
                    </>
                  )}
                  <hr />
                  <br />
                  {tablesDetails && tablesDetails.length > 0 && (
                    <>
                      <p className="sub_title_ mt-4 mb-3">Table Details</p>
                      {!reservationLoading ? (
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: "60%" }}>Table Name</th>
                              <th style={{ width: "40%" }}>Chairs</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tablesDetails.map((table) => (
                              <tr key={table.id}>
                                <td>
                                  <strong>{table.name}</strong>
                                </td>
                                <td>
                                  {table.chairs}{" "}
                                  {Number(table.chairs) === 1
                                    ? "chair"
                                    : "chairs"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <Skeleton width={200} height={20} />
                      )}
                    </>
                  )}
                  <br />
                  {!expired && !isEdit && (
                    <Fragment>
                      {reservDetails?.status == "Requested" ||
                      reservDetails?.status == "Modified" ? (
                        <div className="btn_grp_">
                          <button
                            type="button"
                            className="view_btns_ accept_btn_"
                            onClick={handleApproveReservation}
                            disabled={enquiryActionLoading}
                          >
                            <i>
                              {" "}
                              <Ai.AiOutlineCheckCircle />
                            </i>
                            Accept
                          </button>
                          <button
                            type="button"
                            className="view_btns_ reject_btn_"
                            onClick={() => setShowModal(true)}
                            disabled={enquiryActionLoading}
                          >
                            <i>
                              {" "}
                              <Ai.AiOutlineCloseCircle />
                            </i>
                            Reject
                          </button>
                        </div>
                      ) : reservDetails?.status == "Approved" ? (
                        <div class="text-success text-center" role="alert">
                          Reservation is Approved
                        </div>
                      ) : reservDetails?.status == "Cancelled" ? (
                        <div class="text-danger text-center" role="alert">
                          Cancelled Reservation!
                        </div>
                      ) : (
                        ""
                      )}
                    </Fragment>
                  )}
                </div>
              </div>
            </TabPanel>
            <TabPanel>
              <ReservationMessages
                reservDetails={reservDetails}
                id={id}
                messageList={messageList}
                getNewMessages={getNewMessages}
              />
            </TabPanel>
          </Tabs>
        </div>
      </MasterLayout>
    </Fragment>
  );
}

export default ViewReservation;
